import { PropsWithChildren } from 'react';

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import type { AxiosError } from 'axios';
import { sentryLogger } from '@shared/lib/helpers/sentryLogger';

const SKIP_STATUSES = [400, 401, 403, 404, 409, 422, 429];

const shouldLog = (error: AxiosError) => {
  if (!error.status) return true;
  return !SKIP_STATUSES.includes(error.status);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (shouldLog(error)) {
        sentryLogger(error, {
          extras: {
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            meta: query.meta,
            state: {
              status: query.state.status,
              fetchStatus: query.state.fetchStatus,
              dataUpdatedAt: query.state.dataUpdatedAt,
            },
          },
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, _ctx, mutation) => {
      if (shouldLog(error)) {
        sentryLogger(error, {
          extras: {
            mutationKey: mutation.options.mutationKey,
            variables: JSON.stringify(variables ?? ''),
            meta: mutation.options.meta,
          },
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AxiosError;
  }
}

const QueryProvider = ({ children }: Required<PropsWithChildren>) => {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export default QueryProvider;
